import { FC } from 'react';
import {
  ComponentRendering,
  GetStaticComponentProps,
  LayoutServiceData,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';
import { GetStaticPropsContext } from 'next';

import { ComponentProps } from '@gn-audio/jss-components';
import { FamilyGroup, getFamily, getLocaleWithRegion } from '@gn-audio/jss-core';

import { CustomizerConfig, Filter, getCustomizerConfig } from 'api/jabraPublicApi';
import { getFamilyGroupsWithAttributes } from 'api/productCatalogueApi';
import { parseSCId, parseSCLookupValue } from 'common/siteCoreHelpers';
import {
  Meta3dEngravingRenderingUuidAttribute,
  MetaProducttypeAttribute,
  ProductBrandColorAttribute,
  ProductEngravingAttribute,
  ProductPortfolioAttribute,
} from 'composables/Product/Buy/customizerHelpers';
import { ProductCustomizer } from 'composables/Product/Buy/ProductCustomizer';
import { GroupAttributeKeys } from 'utils/productUtils';

import { Product } from '../../../../sitecoreModels/Product';

type HeroCustomizerProps = {
  fields: Product;
  uniqueSellingPoint: string;
  groups: FamilyGroup[];
  config: CustomizerConfig;
  error?: undefined;
  layout: LayoutServiceData;
} & ComponentProps;

const HeroCustomizer: FC<HeroCustomizerProps> = ({
  config,
  uniqueSellingPoint,
  groups,
  error,
  fields,
}) => {
  if (error) {
    return <>{JSON.stringify(error)}</>;
  }
  const isMainHeading = fields.IsMainHeading?.value;

  return (
    <ProductCustomizer
      uniqueSellingPoint={uniqueSellingPoint}
      groups={groups}
      config={config}
      isMainHeading={isMainHeading}
    />
  );
};

const filterAttributesInGroups = (groups: FamilyGroup[], filters: Filter[]): FamilyGroup[] => {
  const necessaryAttributes = [
    ...filters.map((filter) => filter.name),
    GroupAttributeKeys.COLOR_HEX,
    GroupAttributeKeys.LIFESTYLE_IMAGE,
    ProductBrandColorAttribute,
    Meta3dEngravingRenderingUuidAttribute,
    ProductPortfolioAttribute,
    MetaProducttypeAttribute,
    ProductEngravingAttribute,
  ];

  return groups.map((group) => {
    const filteredAttributes = group.attributes.filter((attr) =>
      necessaryAttributes.includes(attr.key)
    );
    return { ...group, attributes: filteredAttributes };
  });
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering: ComponentRendering,
  _: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    const { fields: dataSource, params = {} } = rendering;

    const productData = dataSource as unknown as Product;
    const familyCode = productData.Family.value;

    const familyLookupCode = parseSCLookupValue(familyCode);
    const language = getLocaleWithRegion(context.locale);

    const dataSourceId = parseSCId(rendering.dataSource);

    const family = await getFamily(+familyLookupCode.id, language);
    let groups = await getFamilyGroupsWithAttributes(familyLookupCode.id, language);

    if (typeof groups === 'string') {
      groups = [];
    }
    //todo this should be taken from datasource
    const customizerConfig = await getCustomizerConfig(dataSourceId, context.locale);

    return {
      uniqueSellingPoint: sanitize(family?.uniqueSellingPoint || ''),
      groups: filterAttributesInGroups(groups, customizerConfig.filters),
      config: customizerConfig,
      params,
    };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { groups: [], config: {}, params };
  }
};

export default withDatasourceCheck()<HeroCustomizerProps>(HeroCustomizer);
